import React, { useCallback } from 'react';
import cc from 'classnames';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { makeStyles } from '@material-ui/core/styles';
import Form from 'ui/Form/Form';
import Button, { Themes as ButtonThemes } from 'ui/Button/Button';
import { Textarea, AmountInput } from 'ui/Input';
import { onlyNCharacters } from 'utils/formatters';
import ModalWindow from '../ModalWindow/ModalWindow';
import noop from '../../../utils/noop';

import styles from './TransactionsFilterModal.module.css';

interface IFilter {
  type: string,
  from: string,
  to: string,
  narration: string
}

type Props = {
  open: boolean,
  onClose: () => void,
  changeFilters: (values) => void
  filters: IFilter,
  resetFilters: () => void
}

const useStyles = makeStyles({
  label: {
    fontFamily: 'var(--ff-etelka-text)',
    fontSize: '12px',
    lineHeight: '17px',
    color: '#222736',
    marginRight: '15px',
  },
});

const TransactionsFilterModal = ({ open, onClose, changeFilters, filters, resetFilters }: Props) => {
  const [type, setType] = React.useState('All');

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setType((event.target as HTMLInputElement).value);
  };

  const onSubmit = useCallback(
    (values) => {
      changeFilters({ ...values, type });
      onClose();
    },
    [type],
  );

  const clearFilters = () => {
    resetFilters();
    setType('All');
    onClose();
  };

  const classes = useStyles();

  return (
    <>
      <ModalWindow title="Transaction History Filter" open={open} onClose={onClose}>
        <Form
          onSubmit={onSubmit}
          initialValues={filters}
          render={({ handleSubmit }) => (
            <form onSubmit={handleSubmit} noValidate>
              <div className={styles.fieldWrapper}>
                <span className={styles.label}>Type of operation</span>
                <RadioGroup
                  value={type}
                  onChange={handleChange}
                  className={styles.radioWrapper}
                >
                  <FormControlLabel
                    value="All"
                    control={<Radio color="primary" size="small" />}
                    label={<span className={classes.label}>All</span>}
                  />
                  <FormControlLabel
                    value="C"
                    control={<Radio color="primary" size="small" />}
                    label={<span className={classes.label}>Credit</span>}
                  />
                  <FormControlLabel
                    value="D"
                    control={<Radio color="primary" size="small" />}
                    label={<span className={classes.label}>Debit</span>}
                  />
                </RadioGroup>
              </div>
              <div className={styles.fieldWrapper}>
                <span className={styles.label}>Amount range</span>
                <div className={styles.inputsWrapper}>
                  <AmountInput name="from" label="From" />
                  <AmountInput name="to" label="To" />
                </div>
              </div>
              <div className={cc(styles.fieldWrapper, styles.noBorder)}>
                <span className={styles.label}>Narration</span>
                <Textarea
                  name="narration"
                  label="Narration"
                  fieldProps={{
                    parse: onlyNCharacters(25),
                  }}
                />
              </div>
              <div className={styles.buttonsWrapper}>
                <Button
                  type="button"
                  theme={ButtonThemes.secondary}
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button type="submit" theme={ButtonThemes.primary}>
                  Apply
                </Button>
              </div>
              <div
                role="button"
                tabIndex={0}
                className={styles.resetButton}
                onClick={clearFilters}
                onKeyPress={noop}
              >
                Reset Filter
              </div>
            </form>
          )}
        />
      </ModalWindow>
    </>
  );
};

export default TransactionsFilterModal;
